/**
 * GerencialLayout — Sidebar secundaria (desktop) + Tabs scrolleables (mobile).
 *
 * Espacio dedicado a las preguntas de dirección: proyecciones de caja,
 * comisiones a pagar en el horizonte, decisiones estratégicas.
 *
 * NO es una sub-sección de Finanzas — la gerencia no debería tener que
 * aprender el mapa operativo de Finanzas para ver "cuánto le debo a las
 * agencias el resto del mes". Vive a nivel de módulo top-level, con su
 * propia audiencia (admin/superadmin).
 *
 * Mismo patrón visual que FinanzasLayout / AgenciasLayout / BookingLayout.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Tabs, Tab,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Rol del usuario logueado — controla qué items son visibles.
function _getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

// Secciones. Empieza con "Planeación" (proyección de pagos como primer item).
// A medida que se agreguen más vistas gerenciales (KPIs mes, cartera vencer,
// rendimiento por agencia, etc.) van acá — este archivo es el único punto de
// entrada al menú.
const SECCIONES = [
  {
    grupo: 'Planeación',
    color: '#7c3aed',
    items: [
      { path: 'proyeccion-pagos', label: 'Proyección de pagos', icon: <TrendingUpIcon />,
        allowedRoles: ['admin', 'superadmin'] },
    ],
  },
];

// Filtrar items por rol antes de flatten — items con allowedRoles definido
// solo aparecen si el rol del usuario está en la lista.
function _filtrarPorRol(secciones, role) {
  return secciones.map(s => ({
    ...s,
    items: s.items.filter(i => !i.allowedRoles || i.allowedRoles.includes(role)),
  })).filter(s => s.items.length > 0);
}

export default function GerencialLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const rolUsuario = _getUserRole();
  const secciones = _filtrarPorRol(SECCIONES, rolUsuario);
  const todosLosItems = secciones.flatMap(s =>
    s.items.map(i => ({ ...i, grupoColor: s.color, grupo: s.grupo }))
  );

  // Redirigir a "proyeccion-pagos" si entran a /gerencial sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/gerencial' || location.pathname === '/gerencial/') {
      if (todosLosItems.length > 0) {
        navigate(`/gerencial/${todosLosItems[0].path}`, { replace: true });
      }
    }
  }, [location.pathname, navigate, todosLosItems]);

  const activeIdx = Math.max(0, todosLosItems.findIndex(
    s => location.pathname.includes(`/gerencial/${s.path}`)
  ));
  const itemActivo = todosLosItems[activeIdx];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario (desktop) */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Gerencial
          </Typography>
        </Box>

        <List sx={{ px: 1, py: 0, flex: 1, overflowY: 'auto' }} dense>
          {secciones.map(seccion => (
            <Box key={seccion.grupo}>
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <Typography sx={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8,
                  textTransform: 'uppercase', color: seccion.color,
                }}>
                  {seccion.grupo}
                </Typography>
              </Box>
              {seccion.items.map(item => {
                const isActive = location.pathname.includes(`/gerencial/${item.path}`);
                return (
                  <ListItemButton key={item.path}
                    onClick={() => navigate(`/gerencial/${item.path}`)}
                    sx={{
                      borderRadius: 2, mb: 0.25, py: 0.75, pl: 1.5,
                      bgcolor: isActive ? `${seccion.color}15` : 'transparent',
                      borderLeft: isActive ? `3px solid ${seccion.color}` : '3px solid transparent',
                      '&:hover': { bgcolor: isActive ? `${seccion.color}25` : '#f1f5f9' },
                    }}>
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? seccion.color : '#94a3b8' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13, fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#0f172a' : '#475569',
                      }} />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}
        </List>

        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Planeación de caja, comisiones proyectadas y decisiones estratégicas.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <Tabs
            value={activeIdx}
            onChange={(_, idx) => navigate(`/gerencial/${todosLosItems[idx].path}`)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 700, fontSize: 12 },
              '& .MuiTabs-indicator': {
                backgroundColor: itemActivo?.grupoColor || '#7c3aed',
                height: 3, borderRadius: 2,
              },
            }}
          >
            {todosLosItems.map(item => (
              <Tab key={item.path} icon={item.icon} iconPosition="start" label={item.label} />
            ))}
          </Tabs>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}
